import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Watch,
  Check,
  Loader2,
  HeartPulse,
  BedDouble,
  Droplets,
  Activity,
} from "lucide-react";
import { setPageMeta } from "@/lib/pageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: HeartPulse, text: "Continuous heart-rate & HRV trends without manual typing" },
  { icon: BedDouble, text: "Automatic sleep tracking — no more logging hours by hand" },
  { icon: Droplets, text: "Smart hydration nudges tuned to your day" },
  { icon: Activity, text: "A live lifestyle index that updates as your day unfolds" },
];

export default function Waitlist() {
  const [params] = useSearchParams();
  const source = params.get("source") || "tools";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta({
      title: "Smartwatch Sync — Free Launch Waitlist | SilentScan AI",
      description:
        "Join the free waitlist for automatic smartwatch-synced wellness tracking: continuous heart rate, sleep and hydration monitoring. SilentScan AI is free for everyone.",
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/waitlist`, { email: email.trim(), source });
      setDone(true);
      toast.success("You're on the waitlist");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join the waitlist. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="waitlist-page" className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-2xl mx-auto px-6 pt-24 pb-16">
        <Link
          to="/tools"
          data-testid="waitlist-back"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to free tools
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_30px_rgba(0,229,255,0.35)]">
            <Watch className="w-7 h-7 text-white" />
          </div>
          <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
            Coming soon · Free for everyone
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            Smartwatch Sync
            <span className="text-zinc-500"> — tracking that types itself.</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed">
            SilentScan AI is completely free — no accounts, no payments. Leave
            your email and we&apos;ll let you know the moment automatic
            smartwatch tracking launches.
          </p>

          <div className="mt-8 space-y-3">
            {PERKS.map((p, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
                  <p.icon className="w-4 h-4 text-cyan-300" />
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-3xl border border-cyan-400/25 bg-gradient-to-br from-[#0B3D91]/20 to-cyan-500/[0.08] p-6">
            {done ? (
              <div data-testid="waitlist-success" className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center shrink-0">
                  <Check className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold">You're on the list.</div>
                  <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
                    We'll reach out the moment Smartwatch Sync opens. Meanwhile,
                    everything on SilentScan AI is free — no sign-up needed.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit}>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                  Email address
                </label>
                <div className="mt-2 flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="waitlist-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 rounded-full bg-black/40 border border-white/10 focus:border-cyan-400/50 outline-none px-5 py-3 text-sm text-white placeholder:text-zinc-600"
                  />
                  <button
                    data-testid="waitlist-submit-btn"
                    type="submit"
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full btn-glow text-white text-sm font-medium disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join the free waitlist"}
                  </button>
                </div>
                <p className="mt-3 text-[11px] text-zinc-500">
                  No spam, no charge — one email when the feature launches.
                </p>
              </form>
            )}
          </div>

          <p className="mt-6 text-[11px] text-zinc-500 leading-relaxed">
            Already have a Bluetooth heart-rate band?{" "}
            <Link to="/wearables" className="text-cyan-400 hover:underline">
              Try the free live pairing
            </Link>{" "}
            available today.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
